import { Suspense, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { currentTheme } from './reducers/sliceReducer'
import { lightTheme, darkTheme } from './utilities/themes'
import { BrowserRouter as Router } from 'react-router-dom'
import { renderRoutes } from 'react-router-config'
import { routes } from './routes'
import { AlertContextProvider } from './contexts/AlertContext'
import { CustomerContextProvider } from './contexts/CustomerContext'
import { SnackbarProvider } from './contexts/SnackbarContext'
import { userLogout } from './actions/userActions'
import { parseJwt } from './utilities/common'
import { setGa4UserId } from './utilities/analytics'
import Snackbar from './components/Snackbar'
import InfoDialog from '@/components/InfoDiaglog'
import InfoDialogAsync from '@/components/InfoDialogAsync'
import Alert from './components/Alert'
import ErrorBoundary from './components/ErrorBoundary'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { TooltipProvider } from '@/components/ui/tooltip'

function App() {
  const isDarkTheme = useSelector(currentTheme)
  const theme = isDarkTheme ? darkTheme : lightTheme
  const token = window.localStorage.getItem('auth_token')
  const lastActivityTime = useRef(new Date())

  useEffect(() => {
    const checkUserActivity = () => {
      const currentTime = new Date()
      const inactiveTime = currentTime - lastActivityTime.current

      // Check token expiration
      if (token) {
        const exp = parseJwt(token).exp
        const timeLeft = exp - Math.floor(currentTime / 1000)
        if (timeLeft < 0) {
          userLogout()
        }
      }

      // Check if 30 minutes have passed
      if (token && (inactiveTime >= 30 * 60 * 1000)) {
        userLogout()
      }
    }
    checkUserActivity()

    const activityTimer = setInterval(checkUserActivity, 60000)
    return () => clearInterval(activityTimer)
  }, [token])

  useEffect(() => {
    const gaUserId = window.localStorage.getItem('ga_user_id')
    if (token && gaUserId) setGa4UserId(gaUserId)
  }, [])

  useEffect(() => {
    function handleUserActivity() {
      lastActivityTime.current = new Date()
    }
    window.addEventListener('mousemove', handleUserActivity)
    window.addEventListener('keydown', handleUserActivity)

    return () => {
      window.removeEventListener('mousemove', handleUserActivity)
      window.removeEventListener('keydown', handleUserActivity)
    }
  }, [])

  return (
    <ThemeProvider theme={theme}>
      {/*
        TooltipProvider wraps the whole app so any shadcn/ui Tooltip can be
        used without re-wrapping it locally. Radix's Tooltip.Root/Trigger/
        Content read this context; omitting it throws at render time.
      */}
      <TooltipProvider>
        <AlertContextProvider>
          <CustomerContextProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <SnackbarProvider>
                <Router>
                  <ErrorBoundary>
                    {/*
                      Outer Suspense: full-screen fallback for pre-auth lazy routes
                      (/login, /forgot-password, /reset-password). For authenticated
                      routes the inner Suspense in Auth.jsx (inside <Frame>) catches
                      first, so only the content area shows the spinner while the
                      sidebar/topbar stay mounted. The two boundaries are intentional —
                      do not consolidate.
                    */}
                    <Suspense
                      fallback={
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100vh'
                          }}
                        >
                          <CircularProgress />
                        </Box>
                      }
                    >
                      {renderRoutes(routes)}
                    </Suspense>
                  </ErrorBoundary>
                </Router>
                <CssBaseline />
                <Snackbar />
                <Alert />
              </SnackbarProvider>
              <InfoDialog.Provider />
              <InfoDialogAsync.Provider />
            </LocalizationProvider>
          </CustomerContextProvider>
        </AlertContextProvider>
      </TooltipProvider>
    </ThemeProvider>
  )
}

export default App
